import {
  useState,
  useEffect,
  useRef,
  type ReactElement,
  type ReactNode,
  type CSSProperties,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Menu,
  Home,
  Compass,
  Search,
  BookOpen,
  Plus,
  User as UserIcon,
  Crown,
  LogOut,
  Lock,
  X,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Library,
  HelpCircle,
  MessageSquare,
  Sun,
  Moon,
  type LucideIcon,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import AuthModal from "./AuthModal";
import NotificationBell from "./NotificationBell";
import Logo from "./Logo";
import Wordmark from "./Wordmark";
import WhatsNewModal from "./WhatsNewModal";
import ThemeToggle from "./ThemeToggle";
import { useIsMobile } from "@/hooks/useMediaQuery";
import useNotifications from "@/hooks/useNotifications";
import whatsNewData from "@/data/whatsNew.json";

import type { User } from "@/types/user";

// Constants for What's New notification tracking
const WHATS_NEW_STORAGE_KEY = "tellor_lastSeenWhatsNew";

// One-time migration from old localStorage key
if (typeof window !== "undefined") {
  const old = localStorage.getItem("storylab_lastSeenWhatsNew");
  if (old && !localStorage.getItem(WHATS_NEW_STORAGE_KEY)) {
    localStorage.setItem(WHATS_NEW_STORAGE_KEY, old);
    localStorage.removeItem("storylab_lastSeenWhatsNew");
  }
}
const LATEST_ANNOUNCEMENT_ID = whatsNewData.announcements[0]?.id || "";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PageLayout component props
 */
interface PageLayoutProps {
  children: ReactNode;
  hideInFullscreen?: boolean;
}

/**
 * Navigation item configuration
 */
interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  authRequired: boolean;
  badge?: string;
}

/**
 * Sidebar content props
 */
interface SidebarContentProps {
  onClose?: () => void;
  collapsed?: boolean;
}

/**
 * Helper to get full image URL (handles both local /static/ paths and external URLs)
 */
const getImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith("/static/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

/**
 * PageLayout - Responsive layout wrapper for all pages
 * Handles mobile hamburger menu, sidebar drawer, and desktop fixed sidebar
 */
const PageLayout = ({
  children,
  hideInFullscreen = false,
}: PageLayoutProps): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    // Initialize from localStorage, default to false (expanded)
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });
  // Initialize user from localStorage cache to prevent auth flicker
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  // If we have cached user, don't show loading state (assume auth will succeed)
  const [loading, setLoading] = useState<boolean>(() => {
    const cached = localStorage.getItem("cached_user");
    return !cached;
  });
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [showWhatsNew, setShowWhatsNew] = useState<boolean>(false);
  const [showHelpDropdown, setShowHelpDropdown] = useState<boolean>(false);
  const [hasUnseenWhatsNew, setHasUnseenWhatsNew] = useState<boolean>(false);
  const helpDropdownRef = useRef<HTMLDivElement>(null);

  // Notifications - called once here, passed down to all NotificationBell instances
  const isAuthenticated = !!user;
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    markAsRead,
    markAllAsRead,
    clearAll,
  } = useNotifications(isAuthenticated);

  // Persist sidebar collapsed state
  const toggleSidebarCollapsed = (): void => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close help dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        helpDropdownRef.current &&
        !helpDropdownRef.current.contains(event.target as Node)
      ) {
        setShowHelpDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Check for unseen What's New updates on mount
  useEffect(() => {
    const lastSeen = localStorage.getItem(WHATS_NEW_STORAGE_KEY);
    // Show badge if user hasn't seen the latest announcement
    if (!lastSeen || lastSeen !== LATEST_ANNOUNCEMENT_ID) {
      setHasUnseenWhatsNew(true);
    }
  }, []);

  // Mark What's New as seen when modal opens
  useEffect(() => {
    if (showWhatsNew && LATEST_ANNOUNCEMENT_ID) {
      localStorage.setItem(WHATS_NEW_STORAGE_KEY, LATEST_ANNOUNCEMENT_ID);
      setHasUnseenWhatsNew(false);
    }
  }, [showWhatsNew]);

  const checkAuth = async (): Promise<void> => {
    try {
      const response = await axios.get<User>(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
      // Cache user data to prevent auth flicker on next page load
      localStorage.setItem("cached_user", JSON.stringify(response.data));
    } catch (error) {
      setUser(null);
      // Clear cached user on auth failure
      localStorage.removeItem("cached_user");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await axios.post(
        `${API}/auth/logout`,
        {},
        {
          withCredentials: true,
        }
      );
      // Clear localStorage token and cached user
      localStorage.removeItem("session_token");
      localStorage.removeItem("cached_user");
      setUser(null);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear local state on error
      localStorage.removeItem("session_token");
      localStorage.removeItem("cached_user");
      setUser(null);
      navigate("/");
    }
  };

  const handleAuthSuccess = (): void => {
    checkAuth();
    navigate("/home");
  };

  const navItems: NavItem[] = [
    { icon: Home, label: "Home", path: "/home", authRequired: false },
    { icon: Plus, label: "Create", path: "/create", authRequired: false },
    { icon: Compass, label: "Explore", path: "/explore", authRequired: false },
    { icon: Search, label: "Search", path: "/search", authRequired: false },
    { icon: BookOpen, label: "Library", path: "/library", authRequired: true },
    {
      icon: Library,
      label: "Series",
      path: "/series",
      authRequired: true,
      badge: "Beta",
    },
  ];

  const isActive = (path: string): boolean => location.pathname === path;

  const handleNavClick = (item: NavItem): void => {
    if (item.authRequired && !user) {
      setShowAuthModal(true);
    } else {
      navigate(item.path);
    }
  };

  // Sidebar content - shared between mobile drawer and desktop fixed sidebar
  // collapsed prop is only used for desktop sidebar
  const SidebarContent = ({
    onClose,
    collapsed = false,
  }: SidebarContentProps): ReactElement => (
    <div className="h-full flex flex-col" style={{ background: "var(--st-bg-surface)" }}>
      {/* Header with Logo and Collapse Toggle */}
      <div className={`border-b ${collapsed ? "p-3" : "p-5"}`} style={{ borderColor: "var(--st-border-light)" }}>
        <div className="flex items-center justify-between">
          {collapsed ? (
            // Collapsed: Show only collapse toggle button
            <button
              onClick={toggleSidebarCollapsed}
              className="w-full p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              title="Expand sidebar"
              aria-label="Expand sidebar"
            >
              <ChevronRight className="w-5 h-5" style={{ color: "var(--st-text-heading)" }} />
            </button>
          ) : (
            <>
              <button
                className="flex items-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 rounded-lg"
                onClick={() => navigate(user ? "/home" : "/")}
                aria-label="Go to home page"
                style={{ background: "transparent", border: "none", padding: 0 }}
              >
                <Wordmark size="md" />
              </button>
              {/* Close button - only on mobile */}
              {onClose ? (
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
                  aria-label="Close sidebar"
                >
                  <X className="w-5 h-5" style={{ color: "var(--st-text-heading)" }} />
                </button>
              ) : (
                // Collapse button - only on desktop
                <button
                  onClick={toggleSidebarCollapsed}
                  className="hidden md:flex p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
                  title="Collapse sidebar"
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeft className="w-5 h-5" style={{ color: "var(--st-text-muted)" }} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* User Profile Section - At top like Suno (only when logged in) */}
      {!loading && user && (
        <div className={`border-b ${collapsed ? "p-2" : "p-4"}`} style={{ borderColor: "var(--st-border-light)" }}>
          {collapsed ? (
            // Collapsed: Avatar only
            <button
              onClick={() => navigate("/profile")}
              className="w-full p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              title={user.name}
              aria-label={`View profile for ${user.name}`}
            >
              {user.picture ? (
                <img
                  src={getImageUrl(user.picture) || ""}
                  alt={user.name}
                  className="w-9 h-9 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: "var(--st-border-light)" }}
                >
                  <UserIcon className="w-5 h-5" style={{ color: "var(--st-primary)" }} />
                </div>
              )}
            </button>
          ) : (
            // Expanded: Avatar + Name + Credits
            <button
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 text-left"
              onClick={() => navigate("/profile")}
              aria-label={`View profile for ${user.name}`}
              style={{ background: "transparent", border: "none" }}
            >
              {user.picture ? (
                <img
                  src={getImageUrl(user.picture) || ""}
                  alt={user.name}
                  className="w-10 h-10 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: "var(--st-border-light)" }}
                >
                  <UserIcon className="w-5 h-5" style={{ color: "var(--st-primary)" }} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-bold truncate"
                  style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                >
                  {user.name}
                </p>
                <div className="flex items-center gap-1">
                  <span
                    className="text-xs"
                    style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
                  >
                    {user.is_premium ? "Premium" : "Free"}
                  </span>
                  <span className="text-xs" style={{ color: "var(--st-border-medium)" }}>
                    •
                  </span>
                  <span
                    className="text-xs font-bold"
                    style={{ fontFamily: "Chewy, cursive", color: "var(--st-primary)" }}
                  >
                    {user.credits} credits
                  </span>
                </div>
              </div>
            </button>
          )}

          {/* Credits badge in collapsed mode */}
          {collapsed && (
            <div
              className="mt-2 p-2 rounded-xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, var(--st-gradient-accent-start) 0%, var(--st-gradient-accent-end) 100%)",
              }}
              title={`${user.credits} credits`}
            >
              <span
                className="text-sm font-bold"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-primary)" }}
              >
                {user.credits}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className={`space-y-2 ${collapsed ? "p-2" : "p-4"}`}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const isLocked = item.authRequired && !user && !loading;

          const buttonStyle: CSSProperties = {
            background: active
              ? "linear-gradient(135deg, var(--st-gradient-accent-start) 0%, var(--st-gradient-accent-end) 100%)"
              : "transparent",
            color: active ? "var(--st-primary)" : isLocked ? "var(--st-text-locked)" : "var(--st-text-heading)",
            fontFamily: "Quicksand, sans-serif",
            fontWeight: active ? 700 : 600,
            cursor: "pointer",
          };

          return (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => handleNavClick(item)}
              title={collapsed ? item.label : undefined}
              aria-label={item.label}
              className={`w-full flex items-center rounded-xl transition-all min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 ${
                isLocked ? "opacity-70" : ""
              } ${collapsed ? "justify-center px-2 py-3" : "gap-3 px-4 py-3"}`}
              style={buttonStyle}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <>
                  <span>{item.label}</span>
                  {item.badge && (
                    <span
                      className="px-1.5 py-0.5 rounded text-xs font-bold"
                      style={{
                        background: "#E0E7FF",
                        color: "#4F46E5",
                        fontSize: "10px",
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                  {isLocked && (
                    <Lock className="w-4 h-4 ml-auto" style={{ color: "var(--st-text-locked)" }} />
                  )}
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* Sign Up CTA - Shown when not logged in (hidden when collapsed) */}
      {!loading && !user && !collapsed && (
        <div className="px-4 pb-4">
          <div
            className="p-4 rounded-2xl relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)",
            }}
          >
            {/* Floating sparkle decoration */}
            <Sparkles
              className="absolute top-3 right-3 w-5 h-5 text-white/40 animate-float"
              style={{ animationDuration: "3s" }}
            />
            <p
              className="font-bold text-sm mb-1"
              style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-on-primary)" }}
            >
              Create Your Story
            </p>
            <p
              className="text-xs mb-3 opacity-80"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-on-primary)" }}
            >
              Sign up to get 1 free credit
            </p>
            <button
              data-testid="sidebar-login-btn"
              onClick={() => setShowAuthModal(true)}
              className="w-full py-2.5 rounded-xl font-bold text-sm min-h-[44px] transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              style={{
                background: "var(--st-bg-surface)",
                color: "var(--st-primary)",
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              Sign Up Free
            </button>
          </div>
        </div>
      )}

      {/* Collapsed Sign Up Icon */}
      {!loading && !user && collapsed && (
        <div className="px-2 pb-2">
          <button
            onClick={() => setShowAuthModal(true)}
            className="w-full p-3 rounded-xl flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
            style={{
              background: "linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)",
            }}
            title="Sign Up Free"
            aria-label="Sign Up Free"
          >
            <UserIcon className="w-5 h-5" style={{ color: "var(--st-text-on-primary)" }} />
          </button>
        </div>
      )}

      {/* Spacer to push bottom section down */}
      <div className="flex-1" />

      {/* Bottom Section - Help, Notifications, Upgrade, Logout */}
      {!loading && user && (
        <div className={`border-t ${collapsed ? "p-2" : "p-4"}`} style={{ borderColor: "var(--st-border-light)" }}>
          {/* Help Dropdown */}
          {collapsed ? (
            // Collapsed: Just the help icon with dropdown
            <div className="mb-2 flex justify-center relative" ref={helpDropdownRef}>
              <button
                onClick={() => setShowHelpDropdown(!showHelpDropdown)}
                className="p-2 rounded-xl transition-all hover:bg-orange-50 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 relative"
                title="Help & Support"
                aria-label={hasUnseenWhatsNew ? "Help & Support (new updates available)" : "Help & Support"}
                aria-expanded={showHelpDropdown}
                aria-haspopup="menu"
              >
                <HelpCircle className="w-5 h-5" style={{ color: "var(--st-primary)" }} />
                {/* Notification dot for unseen What's New */}
                {hasUnseenWhatsNew && (
                  <span
                    className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full animate-pulse"
                    style={{ backgroundColor: "#EF4444" }}
                    aria-hidden="true"
                  />
                )}
              </button>
              {showHelpDropdown && (
                <div
                  className="absolute left-0 bottom-full mb-2 md:bottom-0 md:mb-0 md:left-full md:ml-2 w-full md:w-44 rounded-xl shadow-lg overflow-y-auto z-50"
                  style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)", maxHeight: "min(300px, calc(100vh - 200px))" }}
                  role="menu"
                  aria-label="Help menu"
                >
                  <button
                    onClick={() => {
                      navigate("/help");
                      setShowHelpDropdown(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all hover:bg-orange-50 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-inset"
                    role="menuitem"
                  >
                    <HelpCircle className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                    <span
                      className="text-sm font-medium"
                      style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                    >
                      Help & FAQ
                    </span>
                  </button>
                  <div style={{ borderTop: "1px solid var(--st-border-light)" }} />
                  <button
                    onClick={() => {
                      setShowWhatsNew(true);
                      setShowHelpDropdown(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all hover:bg-orange-50 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-inset"
                    role="menuitem"
                  >
                    <Sparkles className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                    <span
                      className="text-sm font-medium flex-1"
                      style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                    >
                      What's New
                    </span>
                    {hasUnseenWhatsNew && (
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-bold"
                        style={{
                          backgroundColor: "#EF4444",
                          color: "white",
                          fontSize: "10px",
                        }}
                      >
                        NEW
                      </span>
                    )}
                  </button>
                  <div style={{ borderTop: "1px solid var(--st-border-light)" }} />
                  <button
                    onClick={() => {
                      navigate("/contact");
                      setShowHelpDropdown(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all hover:bg-orange-50 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-inset"
                    role="menuitem"
                  >
                    <MessageSquare className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                    <span
                      className="text-sm font-medium"
                      style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                    >
                      Contact Us
                    </span>
                  </button>
                  <div style={{ borderTop: "1px solid var(--st-border-light)" }} />
                  <div
                    className="px-3 py-2 text-xs font-semibold"
                    style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
                  >
                    Theme
                  </div>
                  <ThemeToggle variant="dropdown" showLabel={true} />
                </div>
              )}
            </div>
          ) : (
            // Expanded: Help with label and dropdown
            <div className="mb-3 relative" ref={helpDropdownRef}>
              <button
                onClick={() => setShowHelpDropdown(!showHelpDropdown)}
                className="w-full flex items-center justify-between cursor-pointer transition-all hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 rounded-lg p-1"
                aria-expanded={showHelpDropdown}
                aria-haspopup="menu"
                aria-label="Help & Support"
                style={{ background: "transparent", border: "none" }}
              >
                <span
                  className="text-sm font-bold"
                  style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                >
                  Help
                </span>
                <span
                  className="relative p-2 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  style={{
                    background: showHelpDropdown
                      ? "rgba(255, 140, 97, 0.1)"
                      : "transparent",
                  }}
                >
                  <HelpCircle
                    className="w-6 h-6"
                    style={{ color: showHelpDropdown ? "var(--st-primary)" : "var(--st-text-heading)" }}
                  />
                  {/* Notification dot for unseen What's New */}
                  {hasUnseenWhatsNew && (
                    <span
                      className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full animate-pulse"
                      style={{ backgroundColor: "#EF4444" }}
                      aria-hidden="true"
                    />
                  )}
                </span>
              </button>
              {showHelpDropdown && (
                <div
                  className="absolute left-0 bottom-full mb-2 md:bottom-0 md:mb-0 md:left-full md:ml-2 w-full md:w-44 rounded-xl shadow-lg overflow-y-auto z-50"
                  style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)", maxHeight: "min(300px, calc(100vh - 200px))" }}
                  role="menu"
                  aria-label="Help menu"
                >
                  <button
                    onClick={() => {
                      navigate("/help");
                      setShowHelpDropdown(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all hover:bg-orange-50 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-inset"
                    role="menuitem"
                  >
                    <HelpCircle className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                    <span
                      className="text-sm font-medium"
                      style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                    >
                      Help & FAQ
                    </span>
                  </button>
                  <div style={{ borderTop: "1px solid var(--st-border-light)" }} />
                  <button
                    onClick={() => {
                      setShowWhatsNew(true);
                      setShowHelpDropdown(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all hover:bg-orange-50 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-inset"
                    role="menuitem"
                  >
                    <Sparkles className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                    <span
                      className="text-sm font-medium flex-1"
                      style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                    >
                      What's New
                    </span>
                    {hasUnseenWhatsNew && (
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-bold"
                        style={{
                          backgroundColor: "#EF4444",
                          color: "white",
                          fontSize: "10px",
                        }}
                      >
                        NEW
                      </span>
                    )}
                  </button>
                  <div style={{ borderTop: "1px solid var(--st-border-light)" }} />
                  <button
                    onClick={() => {
                      navigate("/contact");
                      setShowHelpDropdown(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all hover:bg-orange-50 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-inset"
                    role="menuitem"
                  >
                    <MessageSquare className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                    <span
                      className="text-sm font-medium"
                      style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                    >
                      Contact Us
                    </span>
                  </button>
                  <div style={{ borderTop: "1px solid var(--st-border-light)" }} />
                  <div
                    className="px-3 py-2 text-xs font-semibold"
                    style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
                  >
                    Theme
                  </div>
                  <ThemeToggle variant="dropdown" showLabel={true} />
                </div>
              )}
            </div>
          )}

          {/* Divider */}
          <div
            className={`border-t mb-2 ${collapsed ? "mx-1" : ""}`}
            style={{ borderColor: "var(--st-border-light)" }}
          />

          {/* Notification Bell */}
          {collapsed ? (
            // Collapsed: Just the bell icon
            <div className="mb-2 flex justify-center">
              <NotificationBell
                notifications={notifications}
                unreadCount={unreadCount}
                loading={notificationsLoading}
                markAsRead={markAsRead}
                markAllAsRead={markAllAsRead}
                clearAll={clearAll}
              />
            </div>
          ) : (
            // Expanded: Bell with label
            <div className="mb-3 flex items-center justify-between">
              <span
                className="text-sm font-bold"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
              >
                Notifications
              </span>
              <NotificationBell
                notifications={notifications}
                unreadCount={unreadCount}
                loading={notificationsLoading}
                markAsRead={markAsRead}
                markAllAsRead={markAllAsRead}
                clearAll={clearAll}
              />
            </div>
          )}

          {/* Go Premium Button */}
          {!user.is_premium &&
            (collapsed ? (
              <button
                onClick={() => navigate("/pricing")}
                className="w-full mb-2 p-2 rounded-xl flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
                style={{
                  background: "linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)",
                }}
                title="Go Premium"
                aria-label="Go Premium"
              >
                <Crown className="w-4 h-4" style={{ color: "var(--st-text-on-primary)" }} />
              </button>
            ) : (
              <button
                onClick={() => navigate("/pricing")}
                className="w-full mb-3 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
                style={{
                  background: "linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)",
                  color: "var(--st-text-on-primary)",
                }}
              >
                <Crown className="w-4 h-4" />
                Go Premium
              </button>
            ))}

          {/* Logout Button */}
          {collapsed ? (
            <button
              data-testid="sidebar-logout-btn"
              onClick={handleLogout}
              className="w-full p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              title="Log out"
              aria-label="Log out"
            >
              <LogOut className="w-5 h-5" style={{ color: "var(--st-text-muted)" }} />
            </button>
          ) : (
            <button
              data-testid="sidebar-logout-btn"
              onClick={handleLogout}
              className="w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-black/5 dark:hover:bg-white/10 min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              <LogOut className="w-4 h-4" />
              Log out
            </button>
          )}
        </div>
      )}
    </div>
  );

  // When in fullscreen mode, render children without sidebar/header
  if (hideInFullscreen) {
    return (
      <>
        {/* Skip to main content link for keyboard navigation (WCAG 2.4.1) */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
          style={{
            background: "var(--st-bg-surface)",
            color: "var(--st-primary)",
            fontFamily: "Quicksand, sans-serif",
            fontWeight: 600,
          }}
        >
          Skip to main content
        </a>
        <main id="main-content" className="min-h-screen" style={{ background: "var(--st-bg-page)" }} tabIndex={-1}>
          {children}
        </main>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      </>
    );
  }

  return (
    <>
      {/* Skip to main content link for keyboard navigation (WCAG 2.4.1) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
        style={{
          background: "var(--st-bg-surface)",
          color: "var(--st-primary)",
          fontFamily: "Quicksand, sans-serif",
          fontWeight: 600,
        }}
      >
        Skip to main content
      </a>
      <div
        className="flex min-h-screen overflow-x-hidden"
        style={{ background: "var(--st-bg-page)" }}
      >
        {/* Desktop Sidebar - Hidden on mobile */}
        <div
          className={`hidden md:block h-screen fixed left-0 top-0 border-r z-40 transition-all duration-300 ${
            sidebarCollapsed ? "w-[70px]" : "w-64"
          }`}
          style={{ borderColor: "var(--st-border-light)" }}
        >
          <SidebarContent collapsed={sidebarCollapsed} />
        </div>

        {/* Mobile Sidebar - Sheet drawer */}
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="p-0 w-[280px]" hideCloseButton>
            <SidebarContent onClose={() => setSidebarOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Main Content Area */}
        <div
          className={`flex-1 w-full min-w-0 flex flex-col min-h-screen transition-all duration-300 ${
            sidebarCollapsed ? "md:ml-[70px]" : "md:ml-64"
          }`}
        >
          {/* Mobile Header - Only visible on mobile */}
          <div
            className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b"
            style={{ background: "var(--st-bg-page)", borderColor: "var(--st-border-light)" }}
          >
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" style={{ color: "var(--st-text-heading)" }} />
            </button>

            <button
              className="flex items-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 rounded-lg"
              onClick={() => navigate(user ? "/home" : "/")}
              aria-label="Go to home page"
              style={{ background: "transparent", border: "none", padding: 0 }}
            >
              <Wordmark size="sm" />
            </button>

            {/* Notification Bell - only shown when logged in */}
            {user ? (
              <NotificationBell
                notifications={notifications}
                unreadCount={unreadCount}
                loading={notificationsLoading}
                markAsRead={markAsRead}
                markAllAsRead={markAllAsRead}
                clearAll={clearAll}
              />
            ) : (
              <div className="w-[44px]" />
            )}
          </div>

          {/* Page Content */}
          <main id="main-content" className="flex-1 flex flex-col" tabIndex={-1}>
            {children}
          </main>

          {/* Minimal Footer */}
          <footer className="py-6 mt-auto border-t" style={{ borderColor: "var(--st-border-light)" }}>
            <div
              className="flex flex-wrap justify-center gap-4 text-xs"
              style={{ fontFamily: "Quicksand, sans-serif" }}
            >
              <button
                onClick={() => navigate("/help")}
                className="transition-colors hover:opacity-70 focus:outline-none focus-visible:underline focus-visible:text-orange-600"
                style={{ color: "var(--st-text-muted)" }}
              >
                Help
              </button>
              <span style={{ color: "var(--st-border-light)" }}>|</span>
              <button
                onClick={() => navigate("/contact")}
                className="transition-colors hover:opacity-70 focus:outline-none focus-visible:underline focus-visible:text-orange-600"
                style={{ color: "var(--st-text-muted)" }}
              >
                Contact
              </button>
              <span style={{ color: "var(--st-border-light)" }}>|</span>
              <button
                onClick={() => navigate("/terms")}
                className="transition-colors hover:opacity-70 focus:outline-none focus-visible:underline focus-visible:text-orange-600"
                style={{ color: "var(--st-text-muted)" }}
              >
                Terms
              </button>
              <span style={{ color: "var(--st-border-light)" }}>|</span>
              <button
                onClick={() => navigate("/privacy")}
                className="transition-colors hover:opacity-70 focus:outline-none focus-visible:underline focus-visible:text-orange-600"
                style={{ color: "var(--st-text-muted)" }}
              >
                Privacy
              </button>
            </div>
          </footer>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* What's New Modal */}
      <WhatsNewModal
        isOpen={showWhatsNew}
        onClose={() => setShowWhatsNew(false)}
      />
    </>
  );
};

export default PageLayout;
